import React, {Component} from 'react'
import {Modal} from "antd"
import {getDeviseName, getDeviseSymbol} from "../i18n"
import {t} from "@lingui/core/macro"
import {Trans} from "@lingui/react/macro"
import COUNTRY from "country-json/src/country-by-abbreviation.json"

/**
 * MultiDevise — composant racine partagé par tous les thèmes.
 *
 * Gère l'affichage des devises alternatives disponibles pour une page (Don/Form)
 * et la redirection vers l'URL correspondante.
 *
 * Modes :
 * - mode="banner" (défaut)  → bandeau moderne sous la page (anciennement Alert/Button)
 * - mode="modal"            → popup forcée au premier passage, mémorise le choix
 *                              en sessionStorage (par orgId). Liste TOUTES les devises
 *                              disponibles, y compris la devise actuelle.
 *                              sessionStorage évite les exceptions en navigation
 *                              privée et redonne le choix à chaque nouvelle session.
 *
 * Props :
 * - context  : context page (org, lang, ...)
 * - object   : caisse / form (pour lookup des assocs spécifiques par devise)
 * - force    : true → fallback sur defaultAssoc des devisesInterfaces
 * - mode     : "banner" | "modal"
 *
 * Logique métier (getDevisesUrl, getAssoc, changeDevise) inchangée vs l'ancienne version :
 * la refonte porte uniquement sur l'UI.
 */
export default class MultiDevise extends Component{

    state = {
        modalOpen: false,
    }

    componentDidMount() {
        const {mode}=this.props
        if(mode==="modal" && this.shouldShowModal()){
            this.setState({modalOpen:true})
        }
    }

    /**
     * Clé sessionStorage par org pour mémoriser que l'utilisateur a déjà fait
     * un choix sur ce site dans la session courante. On utilise sessionStorage
     * (et pas localStorage) pour éviter les exceptions en navigation privée
     * sur certains navigateurs et redonner le choix à chaque nouvelle session.
     */
    getStorageKey(){
        const {org}=this.props.context
        return "multidevise_choice_"+(org.id || org.orgPrefix || org.orgDomain || "default")
    }

    shouldShowModal(){
        if(typeof window === "undefined") return false
        try{
            return !window.sessionStorage.getItem(this.getStorageKey())
        }catch(e){
            return true
        }
    }

    markChoiceMade(){
        try{
            window.sessionStorage.setItem(this.getStorageKey(),"1")
        }catch(e){}
    }

    closeModal=()=>{
        this.markChoiceMade()
        this.setState({modalOpen:false})
    }

    /**
     * Construit la liste {devise,url} à partir de l'org principale + devisesInterfaces.
     * Sert ensuite à construire l'URL de redirection (remplacement from/to dans l'URL courante).
     */
    getDevisesUrl(org) {
        let devisesUrl=[]

        // URL de la devise PAR DÉFAUT (org.orgDevise) : doit pointer sur la RACINE.
        // On utilise rootDomain/rootPrefix (prefix/domaine racine, capturés côté API
        // AVANT le merge devise). orgDomain/orgPrefix sont écrasés par le merge quand
        // on est servi via une devise alternative → ils pointeraient vers l'alternative
        // courante, rendant `from === to` dans changeDevise (no-op : impossible de
        // revenir à la devise par défaut). Fallback sur orgDomain/orgPrefix si l'API
        // ne renvoie pas encore rootDomain/rootPrefix (rétrocompat avant déploiement).
        const rootDomain=org.rootDomain || org.orgDomain
        const rootPrefix=org.rootPrefix || org.orgPrefix
        let DEVISE_BASE_WEB_URL=null
        if (rootDomain) {
            DEVISE_BASE_WEB_URL = "https://" + rootDomain
        } else if (rootPrefix) {
            DEVISE_BASE_WEB_URL = org.WEB_URL + rootPrefix
        }
        if(DEVISE_BASE_WEB_URL){
            devisesUrl.push({devise:org.orgDevise,url:DEVISE_BASE_WEB_URL})
        }

        if (Array.isArray(org.devisesInterfaces)) {
            for(let di of org.devisesInterfaces) {
                let URL=null
                if (di.domain) {
                    URL = "https://" + di.domain
                } else if (di.prefix) {
                    URL = org.WEB_URL + di.prefix
                }
                if(URL){
                    devisesUrl.push({devise:di.devise,url:URL})
                }
            }
        }

        return devisesUrl
    }

    changeDevise(devise) {
        const {org}=this.props.context
        // Mémorise le choix avant le reload pour que la modal ne réapparaisse pas
        this.markChoiceMade()

        if(devise===org.devise){
            this.setState({modalOpen:false})
            return
        }

        const devisesUrls=this.getDevisesUrl(org)
        const from=devisesUrls.find(i=>i.devise===org.devise)?.url
        const to=devisesUrls.find(i=>i.devise===devise)?.url
        if(!from || !to){
            return
        }
        window.location.href=window.location.href.replace(from,to)
    }

    getAssoc(assocId) {
        const {org}=this.props.context
        return org.cerfasInfos?.find(i=>i.id===assocId)
    }

    getDeviseInterfaceByAssoc(devise) {
        const {org}=this.props.context
        if(Array.isArray(org.devisesInterfaces)){
            return org.devisesInterfaces.find(i=>i.devise===devise)
        }
        return null
    }

    /**
     * Construit la liste des devises pour lesquelles une assoc/URL existe.
     * - includeCurrent=true (modal ET banner) : inclut la devise courante, affichée
     *   grisée + badge « Actuelle » (non cliquable côté banner, fermeture côté modal).
     * - includeCurrent=false : filtre la devise courante (ne propose que des alternatives).
     */
    buildAvailableDevises(includeCurrent=false) {
        const {org}=this.props.context
        const {force,object}=this.props

        const selectedDevise=org.devise
        const baseDevises=Array.isArray(org.devises)?org.devises:[]
        const devises=includeCurrent
            ? baseDevises
            : baseDevises.filter(i=>i!==selectedDevise)

        const out=[]
        for(let d of devises) {
            if(d===org.orgDevise) {
                const assocData=this.getAssoc("MAIN")
                if(assocData) out.push({devise:d,country:assocData._format})
                continue
            }
            if(object) {
                if(object[d]?.assoc && !object[d]?.__disabledChangeDevise){
                    const assocData=this.getAssoc(object[d]?.assoc)
                    if(assocData) out.push({devise:d,country:assocData._format})
                }
            } else if(force) {
                const intefaceData=this.getDeviseInterfaceByAssoc(d)
                if(intefaceData){
                    const assocData=this.getAssoc(intefaceData.defaultAssoc)
                    if(assocData) out.push({devise:d,country:assocData._format})
                }
            }
        }
        return out
    }

    getCountryName(country) {
        return COUNTRY.find(i=>i.abbreviation===country)?.country || t`votre pays`
    }

    /**
     * URL drapeau via flag-icons (lipis/flag-icons sur jsdelivr).
     * Le repo suit strictement ISO 3166-1 alpha-2, mais notre côté admin
     * stocke certains pays avec un code non-ISO (ex: UK pour United Kingdom
     * au lieu du code officiel GB). On normalise ici les exceptions connues.
     */
    getFlagUrl(country) {
        if(!country) return null
        let code=country.toLowerCase()
        // Exceptions : codes côté admin → codes ISO réels pour flag-icons
        const map={uk:"gb"}
        code=map[code] || code
        return "https://cdn.jsdelivr.net/gh/lipis/flag-icons/flags/4x3/"+code+".svg"
    }

    /* ---------- RENDER ---------- */

    render() {
        const {mode}=this.props
        if(mode==="modal"){
            return this.renderModal()
        }
        return this.renderBanner()
    }

    /* ----- BANNER (par défaut) ----- */

    renderBanner() {
        const {context}=this.props
        const {org}=context
        const selectedDevise=org.devise
        // On inclut la devise actuelle dans la liste (affichée grisée avec un badge),
        // mais on n'affiche le bandeau que s'il existe AU MOINS une autre devise à proposer :
        // sinon « Vous résidez dans un autre pays ? » n'aurait aucun sens.
        const availableDevises=this.buildAvailableDevises(true)
        const hasAlternative=availableDevises.some(d=>d.devise!==selectedDevise)

        if(!hasAlternative) return null

        return (
            <>
                <style dangerouslySetInnerHTML={{__html:bannerStyles}}/>
                <div className="md-banner">
                    <div className="md-banner__title">
                        <Trans>Vous résidez dans un autre pays&nbsp;?</Trans>
                    </div>
                    <div className="md-banner__sub">
                        <Trans>Changez de devise pour adapter le paiement et le reçu à votre pays.</Trans>
                    </div>
                    <div className="md-banner__list">
                        {availableDevises.map((d,i)=>{
                            const {devise,country}=d
                            const countryname=this.getCountryName(country)
                            const symbol=getDeviseSymbol(context,devise)
                            const name=getDeviseName(context,devise)
                            const isCurrent=devise===selectedDevise
                            return(
                                <button
                                    key={(country||devise)+"_"+i}
                                    type="button"
                                    className={"md-banner__item"+(isCurrent?" md-banner__item--current":"")}
                                    onClick={isCurrent?undefined:()=>this.changeDevise(devise)}
                                    disabled={isCurrent}
                                    aria-current={isCurrent?"true":undefined}
                                >
                                    <span className="md-banner__flag">
                                        {country
                                            ? <img src={this.getFlagUrl(country)} alt={countryname}/>
                                            : <span className="md-banner__symbol">{symbol}</span>}
                                    </span>
                                    <span className="md-banner__info">
                                        <span className="md-banner__name">
                                            {countryname}
                                            {isCurrent && (
                                                <span className="md-banner__badge">
                                                    <Trans>Actuelle</Trans>
                                                </span>
                                            )}
                                        </span>
                                        <span className="md-banner__country">
                                            {name} <span className="md-banner__symbol-inline">({symbol})</span>
                                        </span>
                                    </span>
                                    {!isCurrent && <span className="md-banner__arrow">→</span>}
                                </button>
                            )
                        })}
                    </div>
                </div>
            </>
        )
    }

    /* ----- MODAL (forcée au load) ----- */

    renderModal() {
        const {context}=this.props
        const {org}=context
        const availableDevises=this.buildAvailableDevises(true)

        // Si une seule (ou zéro) devise dispo : pas de choix à faire, on affiche rien
        if(availableDevises.length<=1) return null

        const selectedDevise=org.devise

        return (
            <Modal
                open={this.state.modalOpen}
                onCancel={this.closeModal}
                footer={null}
                centered
                width={460}
                maskClosable={false}
                closable={true}
                styles={{
                    body:{padding:0},
                    content:{borderRadius:18,overflow:"hidden",padding:0}
                }}
            >
                <style dangerouslySetInnerHTML={{__html:modalStyles}}/>
                <div className="md-modal">
                    <div className="md-modal__header">
                        <div className="md-modal__title">
                            <Trans>Sélectionnez votre devise</Trans>
                        </div>
                        <div className="md-modal__sub">
                            <Trans>Choisissez votre pays pour adapter le paiement et le reçu correspondant.</Trans>
                        </div>
                    </div>
                    <div className="md-modal__list">
                        {availableDevises.map((d,i)=>{
                            const {devise,country}=d
                            const countryname=this.getCountryName(country)
                            const symbol=getDeviseSymbol(context,devise)
                            const name=getDeviseName(context,devise)
                            const isCurrent=devise===selectedDevise
                            return(
                                <button
                                    key={(country||devise)+"_"+i}
                                    type="button"
                                    className={"md-modal__item"+(isCurrent?" md-modal__item--current":"")}
                                    onClick={()=>this.changeDevise(devise)}
                                >
                                    <span className="md-modal__flag">
                                        {country
                                            ? <img src={this.getFlagUrl(country)} alt={countryname}/>
                                            : <span className="md-modal__symbol">{symbol}</span>}
                                    </span>
                                    <span className="md-modal__info">
                                        <span className="md-modal__name">
                                            {countryname}
                                            {isCurrent && (
                                                <span className="md-modal__badge">
                                                    <Trans>Actuelle</Trans>
                                                </span>
                                            )}
                                        </span>
                                        <span className="md-modal__country">
                                            {name} <span className="md-modal__symbol-inline">({symbol})</span>
                                        </span>
                                    </span>
                                    <span className="md-modal__arrow">→</span>
                                </button>
                            )
                        })}
                    </div>
                </div>
            </Modal>
        )
    }
}

const bannerStyles = `
.md-banner {
  margin: 24px auto;
  padding: 22px 22px 18px;
  max-width: 720px;
  border-radius: 18px;
  background: linear-gradient(180deg, #f7fafc 0%, #ffffff 100%);
  border: 1px solid #e5eaf0;
  box-shadow: 0 4px 16px rgba(0,0,0,.04);
}
.md-banner__title {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 4px;
}
.md-banner__sub {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 14px;
}
.md-banner__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.md-banner__item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #e5eaf0;
  cursor: pointer;
  text-align: left;
  width: 100%;
  transition: transform .12s ease, border-color .12s ease, box-shadow .12s ease;
}
.md-banner__item:hover {
  transform: translateY(-1px);
  border-color: var(--primary, #1b579e);
  box-shadow: 0 6px 14px rgba(27,87,158,.10);
}
.md-banner__item--current {
  cursor: default;
  background: #f3f4f6;
  border-color: #e5eaf0;
  opacity: .65;
}
.md-banner__item--current:hover {
  transform: none;
  border-color: #e5eaf0;
  box-shadow: none;
}
.md-banner__item--current .md-banner__flag img { filter: grayscale(.7); }
.md-banner__flag {
  flex-shrink: 0;
  width: 44px; height: 33px;
  border-radius: 6px;
  overflow: hidden;
  background: #f3f4f6;
  box-shadow: 0 0 0 1px rgba(0,0,0,.06), 0 1px 2px rgba(0,0,0,.04);
  display: inline-flex; align-items: center; justify-content: center;
}
.md-banner__flag img {
  width: 100%; height: 100%;
  object-fit: cover;
  display: block;
}
.md-banner__symbol {
  width: 100%; height: 100%;
  background: color-mix(in srgb, var(--primary, #1b579e) 10%, white);
  color: var(--primary, #1b579e);
  display: inline-flex; align-items: center; justify-content: center;
  font-weight: 800; font-size: 18px;
}
.md-banner__info { flex: 1 1 auto; display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.md-banner__name {
  font-size: 14px; font-weight: 700; color: #1a1a2e;
  display: inline-flex; align-items: center; gap: 8px; flex-wrap: wrap;
}
.md-banner__badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: #9ca3af;
  color: white;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
}
.md-banner__country { font-size: 12px; color: #6b7280; }
.md-banner__symbol-inline { color: #9ca3af; font-weight: 600; }
.md-banner__arrow {
  flex-shrink: 0;
  font-size: 18px;
  color: var(--primary, #1b579e);
  opacity: .55;
  transition: opacity .12s ease, transform .12s ease;
}
.md-banner__item:hover .md-banner__arrow { opacity: 1; transform: translateX(2px); }
`

const modalStyles = `
.md-modal { padding: 28px 24px 24px; }
.md-modal__header { text-align: center; margin-bottom: 18px; }
.md-modal__title {
  font-size: 20px;
  font-weight: 800;
  color: #1a1a2e;
  margin-bottom: 6px;
}
.md-modal__sub {
  font-size: 14px;
  color: #6b7280;
  line-height: 1.4;
}
.md-modal__list { display: flex; flex-direction: column; gap: 10px; }
.md-modal__item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 14px;
  border-radius: 14px;
  background: #fff;
  border: 1.5px solid #e5eaf0;
  cursor: pointer;
  text-align: left;
  width: 100%;
  transition: transform .12s ease, border-color .12s ease, box-shadow .12s ease;
}
.md-modal__item:hover {
  transform: translateY(-1px);
  border-color: var(--primary, #1b579e);
  box-shadow: 0 6px 14px rgba(27,87,158,.10);
}
.md-modal__item--current {
  border-color: var(--primary, #1b579e);
  background: color-mix(in srgb, var(--primary, #1b579e) 4%, white);
}
.md-modal__flag {
  flex-shrink: 0;
  width: 48px; height: 36px;
  border-radius: 6px;
  overflow: hidden;
  background: #f3f4f6;
  box-shadow: 0 0 0 1px rgba(0,0,0,.06), 0 1px 2px rgba(0,0,0,.04);
  display: inline-flex; align-items: center; justify-content: center;
}
.md-modal__flag img {
  width: 100%; height: 100%;
  object-fit: cover;
  display: block;
}
.md-modal__symbol {
  width: 100%; height: 100%;
  background: color-mix(in srgb, var(--primary, #1b579e) 10%, white);
  color: var(--primary, #1b579e);
  display: inline-flex; align-items: center; justify-content: center;
  font-weight: 800; font-size: 20px;
}
.md-modal__info { flex: 1 1 auto; display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.md-modal__name {
  font-size: 15px; font-weight: 700; color: #1a1a2e;
  display: inline-flex; align-items: center; gap: 8px; flex-wrap: wrap;
}
.md-modal__symbol-inline { color: #9ca3af; font-weight: 600; }
.md-modal__badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--primary, #1b579e);
  color: white;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
}
.md-modal__country { font-size: 12px; color: #6b7280; }
.md-modal__arrow {
  flex-shrink: 0;
  font-size: 18px;
  color: var(--primary, #1b579e);
  opacity: .55;
  transition: opacity .12s ease, transform .12s ease;
}
.md-modal__item:hover .md-modal__arrow { opacity: 1; transform: translateX(2px); }
`
